import type { SettlePackageOption } from './api'

// Why a package cannot pay for THIS booking, in the operator's words.
//
// ONE definition, because two dialogs ask it — the payment dialog and the
// check-in dialog — and they were already drifting: one had a phrase map, the
// other printed the raw reason code with its underscores swapped for spaces.
// That was survivable while every reason was self-explanatory. It stopped being
// survivable with `insufficient_credits`, which is meaningless without the two
// numbers behind it.

const PHRASES: Record<string, string> = {
  expired: 'expired',
  exhausted: 'no credits left',
  // Per-service sales: THIS service's allowance is spent; other services on the
  // same package may still have credits, so the wording must not read as empty.
  service_exhausted: 'no credits left for this service',
  service_not_covered: 'does not cover this service',
  // The service has moved OUT of the package's category since it was sold, so
  // it has stopped being chargeable against it. Named apart from "does not
  // cover" because the fix is different — the service IS on the package's list,
  // and what changed is the category it now sits in.
  category_mismatch: 'this treatment is no longer in the package’s category',
  voided: 'sale was reversed',
}

/**
 * `insufficient_credits` carries its NUMBERS, and that is the whole reason this
 * function exists rather than a lookup table.
 *
 * A package plainly showing credits left, refusing a booking, reads as a bug
 * unless the line says what the treatment costs. It is also the reason an
 * operator must not read as "exhausted": this package still pays for something
 * smaller, so the answer is a cheaper treatment or money — not a new package.
 */
export function creditIneligibleWhy(p: SettlePackageOption): string {
  if (p.ineligible_reason === 'insufficient_credits') {
    const left = p.sessions_remaining
    return `only ${left} credit${left === 1 ? '' : 's'} left, this treatment costs ${p.credit_cost ?? 1}`
  }
  return PHRASES[p.ineligible_reason ?? ''] ?? 'not usable'
}

/**
 * " · uses 2 credits", or nothing at all when a booking draws the ordinary one.
 *
 * Silent at 1 on purpose: almost every booking costs one credit, and saying so
 * on every row would bury the rows where it is not true. Where it IS more, the
 * operator needs it — otherwise they watch a 10-credit package drop to 8 with
 * no way to tell that from a double debit.
 */
export function creditCostSuffix(p: SettlePackageOption): string {
  const cost = p.credit_cost ?? 1
  return cost > 1 ? ` · uses ${cost} credits` : ''
}
